import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DeviceSpec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  deviceType: string;
  canisterCount: number;
  boxCount: number;
  description: string | null;
  status: string;
  version: number;
};

type DeviceSpecForm = {
  nameZh?: string;
  nameEn?: string;
  deviceType: string;
  canisterCount: number;
  boxCount: number;
  description?: string;
  version?: number;
};

export function StorageDeviceSpecsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<DeviceSpec[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceSpec>();
  const [form] = Form.useForm<DeviceSpecForm>();
  const [messageApi, holder] = message.useMessage();

  const title = (item: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (item.nameEn ?? item.nameZh ?? '')
      : (item.nameZh ?? item.nameEn ?? '');

  const load = async () => {
    setLoading(true);
    try {
      const deviceResponse = await fetch('/api/v1/storage-device-specs', {
        credentials: 'include',
      });
      if (!deviceResponse.ok) throw new Error();
      setItems((await deviceResponse.json()) as DeviceSpec[]);
    } catch {
      await messageApi.error(t('storage.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = () => {
    form.resetFields();
    setEditing(undefined);
    form.setFieldsValue({
      deviceType: 'TANK',
      canisterCount: 6,
      boxCount: 10,
    });
    setOpen(true);
  };

  const edit = (item: DeviceSpec) => {
    setEditing(item);
    form.resetFields();
    form.setFieldsValue({
      deviceType: item.deviceType,
      canisterCount: item.canisterCount,
      boxCount: item.boxCount,
      version: item.version,
      ...(item.nameZh ? { nameZh: item.nameZh } : {}),
      ...(item.nameEn ? { nameEn: item.nameEn } : {}),
      ...(item.description ? { description: item.description } : {}),
    });
    setOpen(true);
  };

  const save = async (values: DeviceSpecForm) => {
    const response = await fetch(
      editing
        ? `/api/v1/storage-device-specs/${editing.id}`
        : '/api/v1/storage-device-specs',
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(values),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('storage.saveFailed'));
      return;
    }
    setOpen(false);
    setEditing(undefined);
    await load();
    await messageApi.success(t('storage.saved'));
  };

  const remove = async (item: DeviceSpec) => {
    const response = await fetch(`/api/v1/storage-device-specs/${item.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('storage.specificationDeleteReferenced')
          : t('storage.deviceSpecDeleteFailed'),
      );
      return;
    }
    await load();
    await messageApi.success(t('storage.deviceSpecDeleted'));
  };

  return (
    <section>
      {holder}
      <Card
        title={t('nav.deviceSpecs')}
        extra={
          <Space>
            <Button type="link" icon={<PlusOutlined />} onClick={create}>
              {t('storage.createDeviceSpec')}
            </Button>
            <Tooltip title={t('storage.refresh')}>
              <Button
                aria-label={t('storage.refresh')}
                type="text"
                shape="circle"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => void load()}
              />
            </Tooltip>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={false}
          columns={[
            { title: t('storage.code'), dataIndex: 'code' },
            {
              title: t('storage.name'),
              render: (_, row: DeviceSpec) => title(row),
            },
            {
              title: t('storage.deviceType'),
              render: (_, row: DeviceSpec) =>
                t(`storage.deviceTypes.${row.deviceType}`),
            },
            { title: t('storage.canisterCount'), dataIndex: 'canisterCount' },
            { title: t('storage.boxCountPerCanister'), dataIndex: 'boxCount' },
            { title: t('storage.status'), dataIndex: 'status' },
            {
              title: t('storage.actions'),
              fixed: 'right' as const,
              width: 180,
              render: (_, row: DeviceSpec) => (
                <Space>
                  <Button
                    type="link"
                    onClick={() => edit(row)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Popconfirm
                    title={t('storage.deviceSpecDeleteConfirm', {
                      name: title(row) ?? row.code,
                    })}
                    okText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    onConfirm={() => void remove(row)}
                  >
                    <Button danger type="link">
                      {t('common.delete')}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
        <Modal
          title={
            editing
              ? t('storage.editDeviceSpec')
              : t('storage.createDeviceSpec')
          }
          open={open}
          okText={t('common.save')}
          cancelText={t('common.cancel')}
          width={720}
          onCancel={() => {
            setOpen(false);
            setEditing(undefined);
          }}
          onOk={() => void form.submit()}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => void save(values)}
          >
            <Row gutter={16}>
              {editing && (
                <Col span={12}>
                  <Form.Item label={t('storage.code')}>
                    <Input value={editing.code} readOnly />
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                <Form.Item
                  name="deviceType"
                  label={t('storage.deviceType')}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={['TANK', 'CABINET', 'OTHER'].map((value) => ({
                      value,
                      label: t(`storage.deviceTypes.${value}`),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="nameZh"
                  label={t('storage.nameZh')}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nameEn" label={t('storage.nameEn')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="description" label={t('storage.description')}>
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="canisterCount"
                  label={t('storage.canisterCount')}
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    min={1}
                    max={500}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="boxCount"
                  label={t('storage.boxCountPerCanister')}
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    min={0}
                    max={500}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="version" hidden>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </section>
  );
}
